import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowLeft, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MeasurementCombobox, PositionCombobox, PositionValueCombobox, FormattedNumberInput, TIER_FILTERS } from "@/components/bullseye/BullseyeFormComponents";

const smartColors = { S: "#22d3ee", M: "#818cf8", A: "#a78bfa", R: "#c084fc", T: "#e879f9" };

function normalizeSmart(val) {
  if (val == null || val === 0) return 0;
  if (val > 5) return Math.round((val / 100) * 5 * 10) / 10;
  return val;
}

function SparkleBurst({ active, originX, originY }) {
  const [particles, setParticles] = useState([]);
  useEffect(() => {
    if (!active) return;
    const colors = ["#34d399","#6ee7b7","#a78bfa","#c4b5fd","#fbbf24","#fcd34d","#22d3ee","#67e8f9"];
    const stars = ["✦","✧","⟡","·"];
    const p = [];
    for (let i = 0; i < 28; i++) {
      const angle = (Math.PI*2*i)/28 + (Math.random()-0.5)*0.5;
      const dist = 60 + Math.random()*120;
      const isStar = Math.random()>0.5;
      p.push({ id:i, x:originX||window.innerWidth/2, y:originY||window.innerHeight/2,
        dx:Math.cos(angle)*dist, dy:Math.sin(angle)*dist-30,
        size:isStar?0:3+Math.random()*4,
        color:colors[Math.floor(Math.random()*colors.length)],
        delay:Math.random()*0.15,
        star:isStar?stars[Math.floor(Math.random()*stars.length)]:null });
    }
    setParticles(p);
    const t = setTimeout(()=>setParticles([]), 1800);
    return ()=>clearTimeout(t);
  }, [active, originX, originY]);
  if (!particles.length) return null;
  return (
    <div style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:9999, overflow:'hidden' }}>
      {particles.map(p => p.star ? (
        <span key={p.id} style={{ position:'absolute', left:p.x, top:p.y, color:p.color, fontSize:14, animation: `sparkle-fly 1.4s ease-out ${p.delay}s forwards`, '--dx':`${p.dx}px`, '--dy':`${p.dy}px` }}>{p.star}</span>
      ) : (
        <div key={p.id} style={{ position:'absolute', left:p.x, top:p.y, width:p.size, height:p.size, borderRadius:'50%', background:p.color, animation: `sparkle-fly 1.4s ease-out ${p.delay}s forwards`, '--dx':`${p.dx}px`, '--dy':`${p.dy}px` }} />
      ))}
    </div>
  );
}

export default function ObjectiveDiscoveryFullPage({
  onClose,
  missionContext = {},
  userDrive = {},
  onSendMessage,
  onFinalizeObjective,
  onReset,
  onStartDiscovery,
  isLoading = false,
  conversationMessages = [],
  scores: externalScores = {},
  currentObjectiveDraft = null,
  isCommander = false,
  targetFields = {},
  onTargetChange = () => {},
  measurements = [],
  positions = [],
  measurementCategories = [],
  positionCategories = [],
  positionSuggestions = [],
}) {
  const [input, setInput] = useState("");
  const [editedStatement, setEditedStatement] = useState("");
  const [showSmart, setShowSmart] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [showDrive, setShowDrive] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const [showSparkle, setShowSparkle] = useState(false);
  const [sparkleOrigin, setSparkleOrigin] = useState({ x: 0, y: 0 });
  const [showHints, setShowHints] = useState(false);
  const [countdown, setCountdown] = useState(30);

  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const lockBtnRef = useRef(null);
  const celebrationRef = useRef(null);

  const scores = {
    bridge: externalScores.bridge_quality || 0,
    smart: externalScores.smart_completeness || 0,
    s: normalizeSmart(externalScores.specific_score),
    m: normalizeSmart(externalScores.measurable_score),
    a: normalizeSmart(externalScores.attainable_score),
    r: normalizeSmart(externalScores.relevant_score),
    t: normalizeSmart(externalScores.timebound_score),
  };

  const draft = currentObjectiveDraft?.statement ? currentObjectiveDraft : null;
  const canFinalize = scores.bridge >= 70 && scores.smart >= 70;
  const avg = (scores.bridge + scores.smart) / 2;
  const phase = finalized ? "bridge" : avg >= 70 ? "bridge" : scores.smart > 30 ? "sharpen" : conversationMessages.length > 2 ? "shape" : "connect";

  const messages = conversationMessages.map(m => ({
    role: m.role === 'user' ? 'u' : 'a',
    text: m.content,
  }));

  const lastAssistantMsg = [...conversationMessages].reverse().find(m => m.role === 'assistant');
  const activeSuggestions = lastAssistantMsg?.suggestions || null;

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [conversationMessages, isLoading]);

  // Only initialize editedStatement from draft when user hasn't started editing
  // Use a ref to track whether the user has made manual edits
  const userHasEdited = useRef(false);
  
  useEffect(() => {
    if (draft && !userHasEdited.current) {
      setEditedStatement(draft.statement);
    }
  }, [draft?.statement]);

  useEffect(() => {
    if (conversationMessages.length === 0 && onStartDiscovery) {
      onStartDiscovery();
    }
  }, []);

  // Show animated hints and scroll to lock button when canFinalize becomes true
  useEffect(() => {
    if (canFinalize && !finalized) {
      setShowHints(true);
      const timer = setTimeout(() => setShowHints(false), 5000);
      // Scroll lock button into view after a short delay for render
      setTimeout(() => {
        if (lockBtnRef.current) {
          lockBtnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowHints(false);
    }
  }, [canFinalize, finalized]);

  const sendMessage = useCallback((txt) => {
    if (isLoading || finalized || !txt?.trim()) return;
    setInput("");
    if (onSendMessage) onSendMessage(txt.trim());
  }, [isLoading, finalized, onSendMessage]);

  const handleReset = () => {
    setFinalized(false);
    setEditedStatement("");
    userHasEdited.current = false;
    setShowSmart(false);
    setShowSparkle(false);
    setInput("");
    if (onReset) onReset();
  };

  const handleFinalize = () => {
    setFinalized(true);
    setCountdown(30);
    if (lockBtnRef.current) {
      const r = lockBtnRef.current.getBoundingClientRect();
      setSparkleOrigin({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    } else {
      setSparkleOrigin({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    }
    setShowSparkle(true);
    setTimeout(() => setShowSparkle(false), 2000);
    if (onFinalizeObjective) onFinalizeObjective(editedStatement);
  };

  // Scroll to celebration when finalized
  useEffect(() => {
    if (finalized && celebrationRef.current) {
      celebrationRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [finalized]);

  // Countdown tick when finalized
  useEffect(() => {
    if (!finalized) return;
    if (countdown <= 0) {
      if (onClose) onClose();
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [finalized, countdown]);

  const phases = [
    { id: "connect", lb: "Connect", ic: "🔗" },
    { id: "shape", lb: "Shape", ic: "🔨" },
    { id: "sharpen", lb: "Sharpen", ic: "🎯" },
    { id: "bridge", lb: "Bridge", ic: "🌉" },
  ];
  const pi = phases.findIndex(p => p.id === phase);

  const smarts = [
    { l: "Specific", k: "S", sc: scores.s, c: "#22d3ee" },
    { l: "Measurable", k: "M", sc: scores.m, c: "#818cf8" },
    { l: "Ambitious/Attainable", k: "A", sc: scores.a, c: "#a78bfa" },
    { l: "Relevant", k: "R", sc: scores.r, c: "#c084fc" },
    { l: "Time-bound", k: "T", sc: scores.t, c: "#e879f9" },
  ];

  const CollapsibleSection = ({ title, icon, open, onToggle, children, badge }) => (
    <div className="rounded-lg border border-slate-800 overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 bg-slate-900/50 hover:bg-slate-800/50 transition-colors">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-300">
          {icon}
          <span>{title}</span>
          {badge && <span className="text-xs font-mono text-indigo-400 ml-1">{badge}</span>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {open && <div className="p-4 border-t border-slate-800">{children}</div>}
    </div>
  );

  return (
    <div className="space-y-4">
      <style>{`
        @keyframes sparkle-fly { 0% { opacity:1; transform:translate(0,0) scale(1); } 70% { opacity:0.8; } 100% { opacity:0; transform:translate(var(--dx),var(--dy)) scale(0); } }
        @keyframes hint-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.7; transform:scale(1.05); } }
        @keyframes hint-bounce { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-4px); } }
        @keyframes hint-fade-in { 0% { opacity:0; transform:translateY(6px); } 100% { opacity:1; transform:translateY(0); } }
        @keyframes fin-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fin-icon-pop { from { opacity: 0; transform: scale(0.3); } to { opacity: 1; transform: scale(1); } }
        @keyframes fin-text-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <SparkleBurst active={showSparkle} originX={sparkleOrigin.x} originY={sparkleOrigin.y} />

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose} className="text-slate-400 hover:text-white h-9 w-9">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Discover Your Objective</h1>
            <p className="text-xs text-slate-500">Bridge mission intent with what drives you</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleReset} title="Reset" className="text-slate-400 hover:text-white h-8 w-8">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Phase indicator */}
      <div className="flex items-center gap-1 flex-wrap">
        {phases.map((p, i) => (
          <div key={p.id} className="flex items-center gap-1">
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold ${
              i < pi ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' :
              i === pi ? 'bg-violet-500/15 text-violet-300 border border-violet-500/40' :
              'bg-slate-800/30 text-slate-600'
            }`}>
              <span>{p.ic}</span><span>{p.lb}</span>
            </div>
            {i < phases.length - 1 && <div className="w-3 h-px bg-slate-700" />}
          </div>
        ))}
      </div>

      {/* Scores summary bar */}
      <div className="flex items-center gap-4 px-3 py-2 rounded-lg bg-slate-900/50 border border-slate-800">
        <button onClick={() => setShowScores(!showScores)} className="flex items-center gap-4 flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 uppercase">Bridge</span>
            <span className="text-sm font-mono font-bold" style={{ color: scores.bridge > 0 ? "#a78bfa" : "#334155" }}>{Math.round(scores.bridge)}%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 uppercase">SMART</span>
            <span className="text-sm font-mono font-bold" style={{ color: scores.smart > 0 ? "#22d3ee" : "#334155" }}>{Math.round(scores.smart)}%</span>
          </div>
          <div className="flex gap-1 ml-auto">
            {["S","M","A","R","T"].map(k => (
              <div key={k} className="w-5 h-5 rounded text-[8px] font-bold flex items-center justify-center"
                style={{ background: `${smartColors[k]}1a`, color: smartColors[k] }}>{k}</div>
            ))}
          </div>
        </button>
        {showScores ? <ChevronUp className="w-4 h-4 text-slate-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />}
      </div>

      {/* Expanded scores */}
      {showScores && (
        <div className="space-y-2 p-3 rounded-lg bg-slate-900/30 border border-slate-800">
          {smarts.map(({ l, k, sc, c }) => {
            const pct = (sc / 5) * 100;
            const on = sc > 0;
            return (
              <div key={k} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded text-[9px] font-bold flex items-center justify-center flex-shrink-0"
                  style={{ background: on ? `${c}1c` : "#33415520", color: on ? c : "#475569" }}>{k}</div>
                <span className="text-xs text-slate-400 w-32 flex-shrink-0 truncate" title={l}>{l}</span>
                <div className="flex-1 h-1.5 rounded bg-slate-800 overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${pct}%`, background: `linear-gradient(90deg,${c},${c}bb)`, transition: 'width 0.7s ease' }} />
                </div>
                {on && <span className="text-[10px] font-mono font-bold flex-shrink-0" style={{ color: c }}>{sc}/5</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Context & Drive collapsibles */}
      <div className="space-y-2">
        <CollapsibleSection
          title="Mission Context"
          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
          open={showContext}
          onToggle={() => setShowContext(!showContext)}
        >
          {missionContext.commanders_intent && (
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase text-indigo-400 mb-1">Commander's Intent</div>
              <p className="text-sm text-slate-300 leading-relaxed">{missionContext.commanders_intent}</p>
            </div>
          )}
          {missionContext.briefing_text && (
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase text-amber-400 mb-1">Theater of War</div>
              <p className="text-sm text-slate-300 leading-relaxed">{missionContext.briefing_text}</p>
            </div>
          )}
          {missionContext.title && (
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Mission</div>
              <p className="text-sm text-slate-400">{missionContext.title}</p>
            </div>
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title="Your Drive"
          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f472b6" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>}
          open={showDrive}
          onToggle={() => setShowDrive(!showDrive)}
        >
          <div className="flex flex-wrap gap-1.5">
            {(userDrive.autonomy || []).map((a, i) => <span key={`a${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">🚀 {a}</span>)}
            {(userDrive.mastery || []).map((m, i) => <span key={`m${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20">🧠 {m}</span>)}
            {(userDrive.purpose || []).map((p, i) => <span key={`p${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/10 text-pink-400 border border-pink-500/20">💡 {p}</span>)}
            {!userDrive.autonomy?.length && !userDrive.mastery?.length && !userDrive.purpose?.length && (
              <span className="text-xs text-slate-600 italic">Complete your profile to see your drive</span>
            )}
          </div>
        </CollapsibleSection>
      </div>

      {/* Celebration Takeover */}
      {finalized && (
        <div ref={celebrationRef} className="flex items-center justify-center w-full rounded-xl overflow-hidden"
          style={{
            background: 'radial-gradient(ellipse at 50% 40%, #0c1a2a 0%, #080e1a 50%, #050810 100%)',
            animation: 'fin-fade-in 0.8s ease',
            minHeight: '400px',
          }}>
          <div className="flex flex-col items-center text-center w-full max-w-[520px] px-8 py-10">
            <div className="w-20 h-20 rounded-full mb-6 flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #059669, #10b981, #34d399)',
                boxShadow: '0 0 40px rgba(16,185,129,0.3), 0 0 80px rgba(16,185,129,0.12)',
                animation: 'fin-icon-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both',
              }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            </div>

            <div className="text-[26px] font-bold leading-tight mb-2"
              style={{
                background: 'linear-gradient(135deg, #34d399, #6ee7b7, #a7f3d0)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                animation: 'fin-text-up 0.5s ease 0.5s both',
              }}>Objective Discovered</div>
            <div className="text-[13px] text-slate-500 mb-7" style={{ animation: 'fin-text-up 0.5s ease 0.6s both' }}>
              You just bridged mission intent to personal drive
            </div>

            <div className="w-full rounded-xl p-[18px_20px] mb-6 text-left"
              style={{
                background: 'linear-gradient(145deg, #0f172af0, #10382860, #0f172af0)',
                border: '1px solid #10b98130',
                animation: 'fin-text-up 0.5s ease 0.7s both',
              }}>
              <div className="inline-flex items-center gap-[5px] text-[9px] font-bold uppercase tracking-[.1em] mb-3 px-[10px] py-[3px] rounded-full"
                style={{ color: '#34d399', background: '#10b98115', border: '1px solid #10b98125' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                Your Objective
              </div>
              <div className="text-[15px] leading-[1.65] font-medium text-slate-200">{editedStatement || draft?.statement}</div>
            </div>

            <div className="w-full rounded-xl p-5 mb-5 text-left"
              style={{
                background: 'linear-gradient(135deg, #1e293b50, #1e293b30)',
                border: '1px solid #334155',
                animation: 'fin-text-up 0.5s ease 0.9s both',
              }}>
              <div className="flex items-center gap-2 text-[14px] font-semibold text-slate-100 mb-[10px]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                This one is yours.
              </div>
              <div className="text-[13px] leading-[1.7] text-slate-400">
                <span className="text-emerald-400 font-semibold">You</span> discovered this. Nobody handed it to you.
                <br/><br/>
                It's now <span className="text-emerald-400 font-semibold">pending commander approval</span>. Once approved — accountability is enhanced with <strong className="text-slate-200 font-semibold">partnership</strong>.
              </div>
            </div>

            <button
              onClick={() => { if (onClose) onClose(); }}
              className="flex items-center justify-center gap-3 w-full max-w-[320px] py-3.5 rounded-xl text-[15px] font-semibold text-white transition-all hover:brightness-110 shadow-lg shadow-emerald-500/20"
              style={{
                background: 'linear-gradient(135deg, #059669, #10b981)',
                animation: 'fin-text-up 0.5s ease 1.1s both',
              }}>
              <span>Let's Go!</span>
              <span className="flex items-center gap-1.5 text-white/70 text-[12px] font-mono">
                <span className="inline-block w-[1px] h-4 bg-white/20" />
                {countdown}s
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Chat conversation */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/30 overflow-hidden">
        <div ref={chatRef} className="max-h-[50vh] overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => m.role === "u" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-br-sm bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm leading-relaxed">{m.text}</div>
            </div>
          ) : (
            <div key={i} className="flex gap-2">
              <div className="w-7 h-7 rounded-full flex-shrink-0 bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5"><path d="M9.5 2C8.12 2 7 3.12 7 4.5V5C5.34 5 4 6.34 4 8c0 .73.27 1.4.71 1.92C3.67 10.6 3 11.7 3 13c0 1.66 1.04 3.08 2.5 3.64V17c0 1.66 1.34 3 3 3h.5"/><path d="M14.5 2C15.88 2 17 3.12 17 4.5V5c1.66 0 3 1.34 3 3 0 .73-.27 1.4-.71 1.92C20.33 10.6 21 11.7 21 13c0 1.66-1.04 3.08-2.5 3.64V17c0 1.66-1.34 3-3 3h-.5"/><path d="M12 2v20"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="px-3 py-2 rounded-2xl rounded-tl-sm bg-slate-800/60 border border-slate-700/30 text-sm text-slate-200 leading-relaxed">{m.text}</div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full flex-shrink-0 bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
              </div>
              <div className="px-3 py-2 rounded-2xl rounded-tl-sm bg-slate-800/40 border border-slate-700/20 flex items-center gap-2">
                <div className="flex gap-1">
                  {[0,1,2].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-bounce" style={{ animationDelay: `${i*0.15}s` }} />)}
                </div>
                <span className="text-[10px] text-slate-500">Reflecting...</span>
              </div>
            </div>
          )}
        </div>

        {/* Objective draft card - positioned below chat, above input */}
        {draft && !finalized && (
          <div className="border-t border-slate-800 p-3">
            <div className="rounded-xl border border-violet-500/30 bg-gradient-to-br from-slate-900 to-violet-950/20 p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                <span className="text-[10px] font-bold uppercase tracking-wider text-violet-400/70">Your Objective</span>
              </div>
              {canFinalize ? (
                <div>
                  <textarea
                    className="w-full text-sm font-medium text-slate-200 bg-slate-800/30 border border-dashed border-violet-500/40 rounded-lg p-3 font-sans resize-none outline-none focus:border-violet-500/70 focus:bg-slate-800/50 transition-all"
                    value={editedStatement}
                    onChange={e => { userHasEdited.current = true; setEditedStatement(e.target.value); }}
                    rows={Math.max(3, Math.ceil(editedStatement.length / 50))}
                  />
                  <p className="text-[9px] text-slate-600 mt-1 relative">
                    Tap to edit directly
                    {showHints && (
                      <span className="absolute -left-1 -top-5 flex items-center gap-1 text-[10px] text-amber-400 font-semibold pointer-events-none" style={{ animation: 'hint-fade-in 0.4s ease-out, hint-bounce 1.2s ease-in-out infinite' }}>
                        <span>👆</span> Edit here!
                      </span>
                    )}
                  </p>
                </div>
              ) : (
                <p className="text-sm font-medium text-slate-200 leading-relaxed">{draft.statement}</p>
              )}
              <div className="relative mt-3">
                <button onClick={() => setShowSmart(!showSmart)}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-slate-800/30 border border-slate-700/30 hover:bg-slate-800/50 transition-colors"
                  style={showHints && !showSmart ? { animation: 'hint-pulse 1.5s ease-in-out infinite', borderColor: 'rgba(251,191,36,0.5)' } : {}}>
                  <div className="flex gap-1">
                    {["S","M","A","R","T"].map(k => (
                      <div key={k} className="w-4 h-4 rounded text-[8px] font-bold flex items-center justify-center"
                        style={{ background: `${smartColors[k]}1a`, color: smartColors[k] }}>{k}</div>
                    ))}
                  </div>
                  <span className="text-[10px] text-slate-400 flex-1 text-left">{showSmart ? "Hide" : "View"} SMART breakdown</span>
                  <span className="text-[10px] text-slate-500">{showSmart ? "▲" : "▼"}</span>
                </button>
                {showHints && !showSmart && (
                  <div className="absolute -right-1 -top-5 flex items-center gap-1 text-[10px] text-amber-400 font-semibold pointer-events-none" style={{ animation: 'hint-fade-in 0.4s ease-out 0.2s both, hint-bounce 1.2s ease-in-out 0.6s infinite' }}>
                    <span>👇</span> Expand to review!
                  </div>
                )}
              </div>
              {showSmart && (
                <div className="mt-2 space-y-1">
                  {[
                    { k: "S", v: draft.specific },
                    { k: "M", v: draft.measurable },
                    { k: "A", v: draft.attainable },
                    { k: "R", v: draft.relevant },
                    { k: "T", v: draft.time_bound },
                  ].filter(x => x.v).map(({ k, v }) => (
                    <div key={k} className="flex gap-2 p-2 rounded bg-slate-800/40 items-baseline">
                      <span className="text-[10px] font-bold w-3 flex-shrink-0 text-center" style={{ color: smartColors[k] }}>{k}</span>
                      <span className="text-[11px] text-slate-400 leading-relaxed">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lock in button - below objective card, above input */}
            {canFinalize && (
              <div className="mt-3 space-y-3">
                {/* Bullseye Target Fields */}
                <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-slate-900 to-amber-950/10 p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#d4a017" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400/70">Bullseye Target</span>
                    <span className="text-[9px] text-slate-600 ml-auto">Structured measurement for chain rule</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-amber-400 mb-1 block">Measurement</Label>
                      <MeasurementCombobox
                        value={targetFields.target_measurement_id || ''}
                        onChange={(vocabId, sel) => {
                          onTargetChange({ ...targetFields, target_measurement_id: vocabId, target_unit: sel?.unit || '' });
                        }}
                        measurements={measurements}
                        categories={measurementCategories}
                        allowedTiers={TIER_FILTERS.objective}
                        className="bg-slate-900 border-slate-700 h-8 w-full text-xs"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-amber-400 mb-1 block">Target</Label>
                        <FormattedNumberInput placeholder="e.g. 12" value={targetFields.target_value || ''}
                          onChange={(v) => onTargetChange({ ...targetFields, target_value: v })}
                          className="bg-slate-900 border-slate-700 text-slate-200 text-xs h-8" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-amber-400 mb-1 block">Unit</Label>
                        <Input value={targetFields.target_unit || ''} placeholder="auto"
                          onChange={(e) => onTargetChange({ ...targetFields, target_unit: e.target.value })}
                          className="bg-slate-900 border-slate-700 text-slate-400 text-xs h-8" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-amber-400 mb-1 block">Position Scope</Label>
                      <PositionCombobox
                        value={targetFields.target_position_type || ''}
                        onChange={(v) => onTargetChange({ ...targetFields, target_position_type: v })}
                        positions={positions}
                        categories={positionCategories}
                        className="bg-slate-900 border-slate-700 h-8 w-full text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-amber-400 mb-1 block">Position Value</Label>
                      <PositionValueCombobox
                        value={targetFields.target_position_value || ''}
                        onChange={(v) => onTargetChange({ ...targetFields, target_position_value: v })}
                        suggestions={positionSuggestions}
                        placeholder="Type or search..."
                        className="bg-slate-900 border-slate-700 h-8 w-full text-xs"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-amber-400 mb-1 block">Target Deadline</Label>
                    <Input type="date" value={targetFields.target_deadline || ''}
                      onChange={(e) => onTargetChange({ ...targetFields, target_deadline: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-slate-200 text-xs h-8 max-w-[200px]" />
                  </div>
                </div>

                <button ref={lockBtnRef} onClick={handleFinalize}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-emerald-500 hover:brightness-110 transition-all shadow-lg shadow-emerald-500/20">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/></svg>
                  Lock In This Objective
                </button>
              </div>
            )}
          </div>
        )}

        {/* Suggestions & input */}
        {!finalized && (
          <div className="border-t border-slate-800 p-3 space-y-2">
            {activeSuggestions && activeSuggestions.length > 0 && !isLoading && (
              <div className="flex flex-wrap gap-1.5">
                {activeSuggestions.map((s, j) => (
                  <button key={j}
                    onClick={() => { setInput(s); if (inputRef.current) inputRef.current.focus(); }}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-colors ${
                      input === s ? 'border-violet-500/60 text-violet-300 bg-violet-500/10' : 'border-slate-700/30 text-slate-400 bg-slate-800/30 hover:border-violet-500/40 hover:text-violet-300'
                    }`}>{s}</button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input ref={inputRef}
                className="flex-1 px-3 py-2.5 rounded-xl text-sm text-slate-200 bg-slate-800/50 border border-slate-700/30 outline-none focus:border-violet-500/50 placeholder:text-slate-600 transition-colors"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); sendMessage(input); } }}
                placeholder={canFinalize ? "Suggest a refinement..." : messages.length <= 1 ? "What excites you about this mission?" : "Share your thoughts..."}
                disabled={isLoading}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                className="w-10 h-10 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-25 text-white flex items-center justify-center flex-shrink-0 transition-colors">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}